import { Skeleton, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import React, { useEffect, useMemo, useState } from 'react';
import StyledTable from '../../commun/table';
import NoData from '../../commun/no-data';
import { SearchCompositionDto } from '../../../services/dtos/search-composition.dto';
import { ResultSearchDto } from '../../../services/dtos/result-search.dto';
import { CompositionsDto } from '../../../services/dtos/compositions.dto';
import { searchCompositions } from '../../../services/composition.service';
import { showAxiosMessageError } from '../../../utils/error-message.util';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import CompositionForm from '../../forms/composition/composition.form';
import { CompositionFormDto } from '../../../services/dtos/composition-form.dto';
import Button from '@mui/material/Button';
import SendMailDialogForm from '../../forms/sendMail/sendMailDialog.form';

/**
 * Tableau de composition indépendant et complet pour un unique groupe élu
 * (ex : Bureau Syndical, Conseil d'Administration), avec sa propre pagination.
 * @constructor
 */
export default function GroupCompositionTableUi(
    props: Readonly<{
        mandateId: string;
        groupId: string;
        fallbackTitle: string;
        canSendMail?: boolean;
    }>
) {
    const showMessage = useSnackbars();
    const [searching, setSearching] = useState<boolean>(false);
    const [loadingError, setLoadingError] = useState<string>();
    const [searchComposition, setSearchComposition] = useState<SearchCompositionDto>({
        page: 0,
        itemsPerPage: 10,
        mandateId: props.mandateId,
        groupId: props.groupId,
    });
    const [resultComposition, setResultaComposition] = useState<ResultSearchDto<CompositionsDto>>();
    const [openSendMail, setOpenSendMail] = React.useState(false);
    const [mails, setMails] = useState<string[]>([]);

    useEffect(() => {
        let isMounted = true;

        setSearching(true);
        setLoadingError(undefined);

        searchCompositions({ ...searchComposition })
            .then(result => {
                if (!isMounted) {
                    return;
                }

                const items = result.items.map(item => ({
                    ...item,
                    name: `${item.firstName} ${item.lastName}`,
                }));
                setResultaComposition({ ...result, items });
            })
            .catch(error => {
                if (!isMounted) {
                    return;
                }

                console.error('Erreur lors de la récupération des compositions');
                setResultaComposition({ items: [], totalItems: 0 });
                setLoadingError('Impossible de charger les élus représentants pour ce groupe.');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setSearching(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [searchComposition, showMessage]);

    const displayedItems = useMemo(() => {
        const items = resultComposition?.items ?? [];

        if (!searchComposition.sort || !searchComposition.order) {
            return items;
        }

        return [...items].sort((left, right) => {
            const leftValue = String(left[searchComposition.sort!] ?? '').toLocaleLowerCase();
            const rightValue = String(right[searchComposition.sort!] ?? '').toLocaleLowerCase();
            const sortResult = leftValue.localeCompare(rightValue, 'fr', {
                numeric: true,
                sensitivity: 'base',
            });

            return searchComposition.order === 'asc' ? sortResult : -sortResult;
        });
    }, [resultComposition?.items, searchComposition.order, searchComposition.sort]);

    const title = resultComposition?.items[0]?.groupLabel || props.fallbackTitle;

    const handlerSearch = (paramForm: CompositionFormDto) => {
        setSearchComposition({
            ...searchComposition,
            paramTitle: paramForm.title,
            paramName: paramForm.name,
            paramLocalAuthority: paramForm.localAuthority,
            page: 0,
        });
    };

    const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setSearchComposition({ ...searchComposition, page: newPage });
    };

    const handleChangeItemsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchComposition({
            ...searchComposition,
            page: 0,
            itemsPerPage: Number.parseInt(event.target.value, 10),
        });
    };

    const handlerSortBy = (order: 'asc' | 'desc', sort: keyof CompositionsDto) => {
        setSearchComposition({ ...searchComposition, order, sort, page: 0 });
    };

    const headCells: HeadCellDto<CompositionsDto, keyof CompositionsDto>[] = [
        { id: 'name', label: 'Nom', sort: true, width: 30 },
        { id: 'title', label: 'Titre', sort: true, width: 16 },
        { id: 'function', label: 'Fonction', sort: false, width: 18 },
        { id: 'localAuthority', label: 'Collectivité', sort: true, width: 36 },
    ];

    const checkMail = (composition: CompositionsDto) => {
        if (composition.mail && !mails.some(value => value === composition.mail)) {
            setMails([...mails, composition.mail]);
        } else if (composition.mail && mails.some(value => value === composition.mail)) {
            setMails(mails.filter(value => value !== composition.mail));
        }
    };

    const checkAllMails = (checked: boolean) => {
        if (!checked) {
            setMails([]);
            return;
        }

        type WithComposition = Required<CompositionsDto>;
        const allMails = resultComposition?.items
            ? resultComposition.items.filter((value): value is WithComposition => !!value.mail).map(value => value.mail)
            : [];
        setMails(allMails);
    };

    const checkAction = () => (
        <Button
            variant="contained"
            data-testid={`check-action-${props.groupId}`}
            disabled={mails.length === 0}
            onClick={() => setOpenSendMail(true)}
        >
            Envoyer un mail
        </Button>
    );

    const showTable = () => {
        if (resultComposition === undefined) {
            return <Skeleton variant="rectangular" height={250}></Skeleton>;
        }
        if (loadingError) {
            return <NoData title={'Information : '} content={loadingError} />;
        }
        if (resultComposition.totalItems === 0) {
            return <NoData title={'Information : '} content={'Aucune donnée trouvée'} />;
        }

        return (
            <StyledTable
                data={displayedItems}
                searching={searching}
                headCells={headCells}
                totalItems={resultComposition.totalItems}
                page={searchComposition.page}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeItemsPerPage}
                itemsPerPage={searchComposition.itemsPerPage}
                check={props.canSendMail ? checkMail : undefined}
                checkAll={props.canSendMail ? checkAllMails : undefined}
                labelCheck={props.canSendMail ? 'Envoyer E-mail' : undefined}
                checkAction={props.canSendMail ? checkAction : undefined}
                handlerSortBy={handlerSortBy}
                order={searchComposition.order}
                sort={searchComposition.sort}
            ></StyledTable>
        );
    };

    return (
        <div>
            <Typography variant="h3" className={'title composition-ui__secondary-title'}>
                <GroupsIcon /> {title}
            </Typography>
            <CompositionForm handlerSearch={handlerSearch} searching={searching} />
            {showTable()}
            <SendMailDialogForm
                open={openSendMail}
                emails={mails}
                mandateId={props.mandateId}
                handleClose={() => setOpenSendMail(false)}
            />
        </div>
    );
}
